import type { ReactNode } from "react";
import {
  Checkbox as AriaCheckbox,
  type CheckboxProps as AriaCheckboxProps,
} from "react-aria-components";
import { cn } from "@core/utils";
import { Icon } from "@core/assets";

interface CheckboxProps extends Omit<
  AriaCheckboxProps,
  "className" | "children"
> {
  children?: ReactNode;
  className?: string;
}

/**
 * Checkbox primitive (own-the-code) on React Aria's `Checkbox`. The box
 * is a rendered element (not the native control) so its checked/focus states are styled from
 * tokens. Uses the render-prop children API for state, avoiding group-variant assumptions.
 */
export function Checkbox({ children, className, ...props }: CheckboxProps) {
  return (
    <AriaCheckbox
      className={cn(
        // `relative` is load-bearing, not cosmetic. React Aria renders the real
        // `<input type="checkbox">` visually hidden with `position: absolute`, so it anchors to
        // the nearest POSITIONED ancestor. Without `relative` here that ancestor is somewhere up
        // the page, and clicking the box focuses an input the browser believes is far away — so
        // it scrolls to "reveal" it. Invisible with one checkbox on screen; very visible in a
        // scrollable list (the wilaya picker jumped on every click).
        "relative flex cursor-pointer items-start gap-xs text-sm-medium text-(--text-secondary) outline-none",
        className
      )}
      {...props}
    >
      {({ isSelected, isIndeterminate, isFocusVisible }) => (
        <>
          <span
            className={cn(
              // mt-0.5 optically centers the 16px box on the first 20px text line, and keeps
              // it top-aligned when the label wraps to multiple lines.
              "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-xs border transition-colors",
              isSelected || isIndeterminate
                ? "border-(--action-primary-bg) bg-action-primary"
                : "border-(--border-field) bg-surface-field",
              isFocusVisible && "ring-2 ring-focus-ring ring-offset-1"
            )}
          >
            {(isSelected || isIndeterminate) && (
              <Icon
                name="check"
                size={12}
                className="text-(--action-primary-fg)"
              />
            )}
          </span>
          {children ? <span>{children}</span> : null}
        </>
      )}
    </AriaCheckbox>
  );
}

export type { CheckboxProps };
